import { Component, Prop, Host, h, Element, Build } from '@stencil/core';

const IMAGE_BASE_TRUST = 'https://marketing-static.signnow.com/349/images/trust-inner/';
const IMAGE_BASE_APPS = 'https://marketing-static.signnow.com/349/images/solutions-catalog/';

/**
 * Transformation section component displaying key SignNow metrics across animated bento-style
 * cards with integration badges. Section heading and description are provided as slotted elements
 * for inline editor support.
 *
 * Card descriptions are localised through the `cardN*` props. Blueprint stores the translations and
 * the render pipeline serialises them onto the element as HTML attributes before SSR, so the
 * translated value binds to the prop in time to render server-side; when no translation exists the
 * prop keeps its English @default.
 *
 * IMPORTANT: the declared attribute name MUST match the pipeline's prop-key casing. The pipeline
 * dash-cases `card1Desc` → `card1-desc` (Stencil's natural casing — no dash before the digit), so
 * each @Prop attribute is `card1-desc`, NOT `card-1-desc`. A mismatch means the translated attribute
 * never binds and every locale silently falls back to the English default.
 *
 * The matching named slots remain supported as an optional inline-editor override (see renderSlotted):
 * non-empty slot content wins, otherwise the prop value (translated or default) is rendered.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @source transformation
 * @slot heading - {required}, <me-heading tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading
 * @slot description - <me-text> - Section description text
 * @slot card-1-desc - {content} Translatable description for card 1 (completion rate). Falls back to card-1-desc prop.
 * @slot card-1-progress - {content} Translatable progress label for card 1 (e.g. "80% completed"). Falls back to card-1-progress prop.
 * @slot card-2-desc - {content} Translatable description for card 2 (average time). Falls back to card-2-desc prop.
 * @slot card-3-desc - {content} Translatable description for card 3 (integrations count). Falls back to card-3-desc prop.
 * @slot card-4-desc - {content} Translatable description for card 4 (average invites). Falls back to card-4-desc prop.
 * @slot card-5-desc - {content} Translatable description for card 5 (education users). Falls back to card-5-desc prop.
 * @slot card-6-desc - {content} Translatable description for card 6 (clicks to sign). Falls back to card-6-desc prop.
 * @slot card-7-desc - {content} Translatable description for card 7 (API calls). Falls back to card-7-desc prop.
 * @example
 *   <sn-transformation-type-1>
 *     <me-heading slot="heading" variant="2" tag="h2" align="center">We spread the word about digital transformation</me-heading>
 *     <me-text slot="description" align="center">signNow empowers users across every industry to embrace seamless and error-free eSignature workflows.</me-text>
 *     <span slot="card-1-desc">completion rate of sent documents</span>
 *     <span slot="card-1-progress">80% completed</span>
 *     <span slot="card-2-desc">average for a sent to signed document</span>
 *     <span slot="card-3-desc">out-of-the-box integrations</span>
 *     <span slot="card-4-desc">average number of signature invites sent in a week</span>
 *     <span slot="card-5-desc">users in Education industry</span>
 *     <span slot="card-6-desc">clicks minimum to sign a document</span>
 *     <span slot="card-7-desc">API calls a week</span>
 *   </sn-transformation-type-1>
 */
@Component({
  tag: 'sn-transformation-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnTransformationType1 {
  @Element() el!: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Card 1 description text.
   * @content
   * @dynamic
   * @default "completion rate of sent documents"
   */
  @Prop({ attribute: 'card1-desc' }) card1Desc: string = 'completion rate of sent documents';

  /**
   * Card 1 progress label text.
   * @content
   * @dynamic
   * @default "80% completed"
   */
  @Prop({ attribute: 'card1-progress' }) card1Progress: string = '80% completed';

  /**
   * Card 2 description text.
   * @content
   * @dynamic
   * @default "average for a sent to signed document"
   */
  @Prop({ attribute: 'card2-desc' }) card2Desc: string = 'average for a sent to signed document';

  /**
   * Card 3 description text.
   * @content
   * @dynamic
   * @default "out-of-the-box integrations"
   */
  @Prop({ attribute: 'card3-desc' }) card3Desc: string = 'out-of-the-box integrations';

  /**
   * Card 4 description text.
   * @content
   * @dynamic
   * @default "average number of signature invites sent in a week"
   */
  @Prop({ attribute: 'card4-desc' }) card4Desc: string = 'average number of signature invites sent in a week';

  /**
   * Card 5 description text.
   * @content
   * @dynamic
   * @default "users in Education industry"
   */
  @Prop({ attribute: 'card5-desc' }) card5Desc: string = 'users in Education industry';

  /**
   * Card 6 description text.
   * @content
   * @dynamic
   * @default "clicks minimum to sign a document"
   */
  @Prop({ attribute: 'card6-desc' }) card6Desc: string = 'clicks minimum to sign a document';

  /**
   * Card 7 description text.
   * @content
   * @dynamic
   * @default "API calls a week"
   */
  @Prop({ attribute: 'card7-desc' }) card7Desc: string = 'API calls a week';

  /**
   * Google Drive integration URL.
   * @default "/integrations/googledrive"
   */
  @Prop({ attribute: 'gdrive-href' }) gdriveHref: string = '/integrations/googledrive';

  /**
   * Microsoft Teams integration URL.
   * @default "/integrations/teams"
   */
  @Prop({ attribute: 'teams-href' }) teamsHref: string = '/integrations/teams';

  /**
   * Microsoft Word integration URL.
   * @default "/integrations/word"
   */
  @Prop({ attribute: 'word-href' }) wordHref: string = '/integrations/word';

  /**
   * Salesforce integration URL.
   * @default "/integrations/salesforce"
   */
  @Prop({ attribute: 'salesforce-href' }) salesforceHref: string = '/integrations/salesforce';

  /**
   * NetSuite integration URL.
   * @default "/integrations/netsuite"
   */
  @Prop({ attribute: 'netsuite-href' }) netsuiteHref: string = '/integrations/netsuite';

  private observer: IntersectionObserver;

  /**
   * Tracks which named slots actually received non-empty assigned content.
   * Herald injects translated <span slot="..."> nodes before SSR, but when a
   * translation is missing it can inject an EMPTY span. An empty assigned node
   * still counts as "slot filled" in Stencil, which hides the slot fallback
   * (the prop default) and leaves the card text blank. We therefore decide per
   * slot whether to render the <slot> (real content present) or the prop value.
   */
  private slotHasContent: Record<string, boolean> = {};

  private readonly slotNames = [
    'card-1-desc',
    'card-1-progress',
    'card-2-desc',
    'card-3-desc',
    'card-4-desc',
    'card-5-desc',
    'card-6-desc',
    'card-7-desc',
  ];

  componentWillLoad() {
    // Runs during SSR (hydrate) and on the client before the first render,
    // while the original light-DOM slotted nodes are still in place.
    this.slotNames.forEach(name => {
      const node = this.el.querySelector(`[slot="${name}"]`);
      this.slotHasContent[name] = !!(node && (node.textContent || '').trim().length);
    });
  }

  /** Render assigned slot content when it's non-empty, otherwise the prop fallback. */
  private renderSlotted(name: string, fallback: string) {
    return this.slotHasContent[name] ? <slot name={name}></slot> : fallback;
  }

  componentDidLoad() {
    if (!Build.isBrowser) return;
    this.initObserver();
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    if (this.observer) this.observer.disconnect();
  }

  private initObserver() {
    if (!Build.isBrowser) return;
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              this.observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 },
      );
      const animatedElements = this.el.querySelectorAll('[data-animate]');
      animatedElements.forEach(el => this.observer.observe(el));
    } else {
      const animatedElements = this.el.querySelectorAll('[data-animate]');
      animatedElements.forEach(el => el.classList.add('is-visible'));
    }
  }

  render() {
    return (
      <Host class="transformation-type-1" data-color-mode={this.colorMode}>
        <section class="me-section transformation-type-1">
          <me-container yGap="2xl" yGapLg="5xl">
            <me-row direction="column" directionLg="column">
          <me-column spanLg="12" class="transformation-type-1__heading-column">
            <div class="transformation-type-1__heading-wrapper">
              <slot name="heading"></slot>
              <slot name="description"></slot>
            </div>
          </me-column>

            <me-column spanLg="12" class="transformation-type-1__cards">
              <div class="transformation-type-1__card transformation-type-1__card--completion-rate">
                <div class="transformation-type-1__card-wrapper">
                  <div class="transformation-type-1__card-content">
                    <me-heading variant="3" class="transformation-type-1__card-title">80%</me-heading>
                    <div class="transformation-type-1__card-description">
                      {this.renderSlotted('card-1-desc', this.card1Desc)}
                    </div>
                  </div>
                  <div class="completion-rate-illustration">
                    <div class="progress" data-animate="fade-in">
                      <div class="progress__title">{this.renderSlotted('card-1-progress', this.card1Progress)}</div>
                      <div class="progress__bar"></div>
                    </div>
                    <div class="document" data-animate="fade-in-up" style={{ transitionDelay: '0.1s' }}>
                      <me-picture srcSet={`${IMAGE_BASE_TRUST}document.png 1x, ${IMAGE_BASE_TRUST}document@2x.png 2x`} webpSrcSet={`${IMAGE_BASE_TRUST}document.webp 1x, ${IMAGE_BASE_TRUST}document@2x.webp 2x`} aspectRatio="auto" alt="Document signing progress illustration" lazy={true} />
                    </div>
                  </div>
                </div>
              </div>

              <div class="transformation-type-1__card transformation-type-1__card--average-sent">
                <div class="transformation-type-1__card-wrapper">
                  <me-heading variant="3" class="transformation-type-1__card-title">1h</me-heading>
                  <div class="transformation-type-1__card-description">
                    {this.renderSlotted('card-2-desc', this.card2Desc)}
                  </div>
                </div>
              </div>

              <div class="transformation-type-1__card transformation-type-1__card--integrations-box">
                <div class="transformation-type-1__card-wrapper">
                  <me-heading variant="3" class="transformation-type-1__card-title">20+</me-heading>
                  <div class="transformation-type-1__card-description">
                    {this.renderSlotted('card-3-desc', this.card3Desc)}
                  </div>
                  <div class="apps">
                    <div class="apps__item" data-animate="fade-in-left" style={{ transitionDelay: '0.1s' }}>
                      <div class="apps__wrapper">
                        <div class="apps__logo"><me-picture class="apps__icon apps__icon--gdrive" srcSet={`${IMAGE_BASE_APPS}g-drive.png 1x, ${IMAGE_BASE_APPS}g-drive@2x.png 2x`} webpSrcSet={`${IMAGE_BASE_APPS}g-drive.webp 1x, ${IMAGE_BASE_APPS}g-drive@2x.webp 2x`} aspectRatio="auto" alt="Google Drive" lazy={true} /></div>
                        <a class="apps__link" href={this.gdriveHref} aria-label="signNow for Google Drive"></a>
                      </div>
                    </div>
                    <div class="apps__item" data-animate="fade-in-down" style={{ transitionDelay: '0.15s' }}>
                      <div class="apps__wrapper">
                        <div class="apps__logo"><me-picture class="apps__icon apps__icon--ms-teams" srcSet={`${IMAGE_BASE_APPS}ms-teams.png 1x, ${IMAGE_BASE_APPS}ms-teams@2x.png 2x`} webpSrcSet={`${IMAGE_BASE_APPS}ms-teams.webp 1x, ${IMAGE_BASE_APPS}ms-teams@2x.webp 2x`} aspectRatio="auto" alt="Microsoft Teams" lazy={true} /></div>
                        <a class="apps__link" href={this.teamsHref} aria-label="signNow for Microsoft Teams"></a>
                      </div>
                    </div>
                    <div class="apps__item" data-animate="fade-in-right" style={{ transitionDelay: '0.2s' }}>
                      <div class="apps__wrapper">
                        <div class="apps__logo"><me-picture class="apps__icon apps__icon--ms-word" srcSet={`${IMAGE_BASE_APPS}ms-word.png 1x, ${IMAGE_BASE_APPS}ms-word@2x.png 2x`} webpSrcSet={`${IMAGE_BASE_APPS}ms-word.webp 1x, ${IMAGE_BASE_APPS}ms-word@2x.webp 2x`} aspectRatio="auto" alt="Microsoft Word" lazy={true} /></div>
                        <a class="apps__link" href={this.wordHref} aria-label="signNow for Word"></a>
                      </div>
                    </div>
                    <div class="apps__item" data-animate="fade-in-up" style={{ transitionDelay: '0.25s' }}>
                      <div class="apps__wrapper">
                        <div class="apps__logo"><me-picture class="apps__icon apps__icon--salesforce" src={`${IMAGE_BASE_APPS}salesforce.svg`} alt="Salesforce" aspectRatio="auto" lazy={true} /></div>
                        <a class="apps__link" href={this.salesforceHref} aria-label="signNow for Salesforce"></a>
                      </div>
                    </div>
                    <div class="apps__item apps__item--double-size" data-animate="fade-in-right" style={{ transitionDelay: '0.3s' }}>
                      <div class="apps__wrapper">
                        <div class="apps__logo"><me-picture class="apps__icon apps__icon--netsuite" srcSet={`${IMAGE_BASE_APPS}netsuite.png 1x, ${IMAGE_BASE_APPS}netsuite@2x.png 2x`} webpSrcSet={`${IMAGE_BASE_APPS}netsuite.webp 1x, ${IMAGE_BASE_APPS}netsuite@2x.webp 2x`} aspectRatio="auto" alt="NetSuite" lazy={true} /></div>
                        <a class="apps__link" href={this.netsuiteHref} aria-label="signNow for Netsuite"></a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="transformation-type-1__card transformation-type-1__card--average-invites">
                <div class="transformation-type-1__card-wrapper">
                  <me-heading variant="3" class="transformation-type-1__card-title">96k</me-heading>
                  <div class="transformation-type-1__card-description">
                    {this.renderSlotted('card-4-desc', this.card4Desc)}
                  </div>
                  <div class="average-invites-illustration" data-animate="fade-in-up" style={{ transitionDelay: '0.1s' }}>
                    <me-picture src={`${IMAGE_BASE_TRUST}notifications.svg`} alt="" aspectRatio="3 / 4" lazy={true} />
                  </div>
                </div>
              </div>

              <div class="transformation-type-1__card transformation-type-1__card--hippa">
                <div class="transformation-type-1__card-wrapper">
                  <div class="transformation-type-1__card-content">
                    <me-heading variant="3" class="transformation-type-1__card-title transformation-type-1__card-title--black">28,9k</me-heading>
                    <div class="transformation-type-1__card-description transformation-type-1__card-description--black">
                      {this.renderSlotted('card-5-desc', this.card5Desc)}
                    </div>
                  </div>
                  <div class="hippa-illustration">
                    <div class="hippa-illustration__item hippa-illustration__item--01" data-animate="fade-in-right" style={{ transitionDelay: '0.2s' }}><me-picture src={`${IMAGE_BASE_TRUST}Security-Badges-01.svg`} alt="" aspectRatio="auto" lazy={true} /></div>
                    <div class="hippa-illustration__item hippa-illustration__item--02" data-animate="fade-in-right" style={{ transitionDelay: '0.3s' }}><me-picture src={`${IMAGE_BASE_TRUST}Security-Badges-02.svg`} alt="" aspectRatio="auto" lazy={true} /></div>
                    <div class="hippa-illustration__item hippa-illustration__item--03" data-animate="fade-in-right" style={{ transitionDelay: '0.4s' }}><me-picture src={`${IMAGE_BASE_TRUST}Security-Badges-03.svg`} alt="" aspectRatio="auto" lazy={true} /></div>
                  </div>
                </div>
              </div>

              <div class="transformation-type-1__card transformation-type-1__card--average-clicks">
                <div class="transformation-type-1__card-wrapper">
                  <div class="transformation-type-1__card-content">
                    <me-heading variant="3" class="transformation-type-1__card-title">2</me-heading>
                    <div class="transformation-type-1__card-description">
                      {this.renderSlotted('card-6-desc', this.card6Desc)}
                    </div>
                  </div>
                  <div class="clicks-illustration" data-animate="fade-in-up" style={{ transitionDelay: '0.1s' }}>
                    <me-picture src={`${IMAGE_BASE_TRUST}cursor-click-select.svg`} alt="" aspectRatio="auto" lazy={true} />
                  </div>
                </div>
              </div>

              <div class="transformation-type-1__card transformation-type-1__card--api-cals">
                <div class="transformation-type-1__card-wrapper">
                  <div class="transformation-type-1__card-content">
                    <me-heading variant="3" class="transformation-type-1__card-title">14.3M</me-heading>
                    <div class="transformation-type-1__card-description">
                      {this.renderSlotted('card-7-desc', this.card7Desc)}
                    </div>
                  </div>
                  <div class="api-cals-illustration">
                    <div class="api-cals-illustration__item api-cals-illustration__item--avatar" data-animate="fade-in-left" style={{ transitionDelay: '0.1s' }}><me-picture src={`${IMAGE_BASE_TRUST}avatar.png`} srcSet={`${IMAGE_BASE_TRUST}avatar.png 1x, ${IMAGE_BASE_TRUST}avatar@2x.png 2x`} webpSrcSet={`${IMAGE_BASE_TRUST}avatar.webp 1x, ${IMAGE_BASE_TRUST}avatar@2x.webp 2x`} alt="Avatar" aspectRatio="auto" lazy={true} /></div>
                    <div class="api-cals-illustration__item api-cals-illustration__item--code" data-animate="fade-in-up" style={{ transitionDelay: '0.2s' }}><me-picture src={`${IMAGE_BASE_TRUST}code.png`} srcSet={`${IMAGE_BASE_TRUST}code.png 1x, ${IMAGE_BASE_TRUST}code@2x.png 2x`} webpSrcSet={`${IMAGE_BASE_TRUST}code.webp 1x, ${IMAGE_BASE_TRUST}code@2x.webp 2x`} alt="Code" aspectRatio="auto" lazy={true} /></div>
                  </div>
                </div>
              </div>
            </me-column>
            </me-row>
          </me-container>
        </section>
      </Host>
    );
  }
}
